import React, { PropsWithChildren, useState } from 'react';
import TabContext, { FetchingData, HidingStrategy, Tab } from '../Context';
import { Tooltip, TooltipPlacement } from '../../../atoms/Tooltip';
import bem from '../../../../lib/bemUtils/bem';
import { KeyEvent } from '../../../../types/KeyEvent';

const TabListClassName = 'tabs__tablist';
const TabClassName = 'tabs__tab';

export const Direction = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
} as const;
export type Direction = (typeof Direction)[keyof typeof Direction];

type TabsProps = PropsWithChildren<{
  direction?: Direction;
  initialActiveTab?: string;
  label?: string;
  hidingStrategy?: HidingStrategy;
  reference?: any;
  fetchingData?: FetchingData;
}>;

function oderKeyCompaer(a: Tab, b: Tab) {
  return a.orderKey || 0 - (b.orderKey || 0);
}

export default function Tabs({
  initialActiveTab,
  direction = Direction.HORIZONTAL,
  children,
  label,
  hidingStrategy = HidingStrategy.REMOVE_FROM_DOM,
  reference,
  fetchingData,
}: TabsProps) {
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | undefined>(initialActiveTab);
  /**
   * Only clicked footer buttons move focus to tabpanel,
   * because they disappear when clicked
   * */
  const [footerButtonsClicked, setFooterButtonsClicked] = useState(false);

  const removeTabById = (tabId: string) => {
    setTabs(prevTabs => {
      const newTabs = prevTabs.filter(tab => tab.id !== tabId);
      if (newTabs.length !== prevTabs.length) {
        return newTabs;
      }
      return prevTabs;
    });
  };

  const enabledTabsOnly = tabs?.filter(tab => !tab.disabled);

  const unregisterTabItem = (tabId: string) => removeTabById(tabId);
  const getTabIndexById = (id: string | undefined) => tabs.findIndex(tab => tab.id === id);
  const getCurrentTabIndex = () => getTabIndexById(activeTabId);
  const getFirstTab = () => enabledTabsOnly[0];
  const getLastTab = () => enabledTabsOnly[enabledTabsOnly.length - 1];
  const hasPreviousTab = () => getCurrentTabIndex() > 0;
  const hasNextTab = (id: string | undefined) => tabs.length > getTabIndexById(id) + 1;
  const getNextTab = (id: string | undefined) => enabledTabsOnly[getTabIndexById(id) + 1] || getFirstTab();
  const getNextTabIndex = (id: string | undefined) => getTabIndexById(id) + 1;
  const getPreviousTab = (id: string | undefined) => enabledTabsOnly[getTabIndexById(id) - 1] || getLastTab();
  const getPreviousTabIndex = (id: string | undefined) => getTabIndexById(id) - 1;
  const isNextTabDisabled = (id: string | undefined) => tabs[getNextTabIndex(id)]?.disabled;
  const isPreviousTabDisabled = (id: string | undefined) => tabs[getPreviousTabIndex(id)]?.disabled;
  const tabExists = (id: string) => tabs.filter(tab => tab.id === id).length === 1;

  const registerOrUpdateTabItem = (tab: Tab) => {
    if (tabExists(tab.id)) {
      // UPDATE the existing tab with the new props and only rerender if
      setTabs(prevTabs => {
        const newTabs = [...prevTabs];
        const tabIndex = prevTabs.findIndex(t => t.id === tab.id);
        // NOTE tabIndex cannot be -1 here due to tabExists
        newTabs[tabIndex] = tab;
        return newTabs.sort(oderKeyCompaer);
      });
    } else {
      // CREATE
      setTabs(prevTabs => [...prevTabs, tab].sort(oderKeyCompaer));
    }
  };

  const focusById = (id: string) => {
    const elem = document.getElementById(id);
    if (elem) {
      elem.focus();
    }
  };

  /**
   * Keyboard navigation on the tabs
   * @param event
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    // preventing shift key defaults because "shift + tab" should work properly, only tab is blocked
    if (!event.shiftKey) {
      event.preventDefault();
    }

    const directionMapping = {
      backward: direction === Direction.HORIZONTAL ? KeyEvent.ArrowLeft : KeyEvent.ArrowUp,
      forward: direction === Direction.HORIZONTAL ? KeyEvent.ArrowRight : KeyEvent.ArrowDown,
    };

    switch (event.code) {
      case directionMapping.forward:
        focusById(`${getNextTab(activeTabId).id}-tab`);
        setActiveTabId(getNextTab(activeTabId).id);
        break;
      case directionMapping.backward:
        focusById(`${getPreviousTab(activeTabId).id}-tab`);
        setActiveTabId(getPreviousTab(activeTabId).id);
        break;
      case KeyEvent.Home:
        focusById(`${getFirstTab().id}-tab`);
        setActiveTabId(getFirstTab().id);
        break;
      case KeyEvent.End:
        focusById(`${getLastTab().id}-tab`);
        setActiveTabId(getLastTab().id);
        break;
      case KeyEvent.Tab:
        // enabling the focusing of the current tab panel via tab key,
        // while shift + tab should do as expected
        if (!event.shiftKey) {
          focusById(`${activeTabId}-tabpanel`);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div ref={reference} className={bem('tabs', direction)}>
      <div role="tablist" aria-orientation={direction} className={TabListClassName} aria-label={label}>
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTabId;
          const isDisabled = index !== 0 && tab.disabled;
          const tabButton = (
            <button
              type="button"
              className={TabClassName}
              key={tab.id}
              ref={tab.ref}
              role="tab"
              aria-selected={activeTabId === tab.id}
              aria-controls={`${tab.id}-tabpanel`}
              aria-disabled={isDisabled || undefined}
              id={`${tab.id}-tab`}
              title={tab.tooltip}
              tabIndex={isActive ? 0 : tab.disabled ? undefined : -1}
              onClick={() => {
                if (!isDisabled) {
                  setActiveTabId(tab.id);
                  setFooterButtonsClicked(false);
                }
              }}
              onKeyDown={onKeyDown}
            >
              {tab.buttonLabel}
            </button>
          );
          return tab.tooltip && tab.ref ? (
            <Tooltip attachTo={tab.ref} hideTooltipFromScreenReader placement={TooltipPlacement.TOP}>
              {tabButton}
            </Tooltip>
          ) : (
            tabButton
          );
        })}
      </div>
      <div className="tabs__tabpanels-container">
        <TabContext.Provider
          value={{
            tabs,
            registerOrUpdateTabItem,
            unregisterTabItem,
            isActiveItem: (id: string) => activeTabId === id,
            setActiveTab: (id: string) => setActiveTabId(id),
            hasPreviousTab,
            hasNextTab,
            focusById,
            getCurrentTabIndexById: getTabIndexById,
            getNextTabIndex,
            getNextTab,
            getPreviousTabIndex,
            getPreviousTab,
            isNextTabDisabled,
            isPreviousTabDisabled,
            hidingStrategy,
            footerButtonsClicked,
            setFooterButtonsClicked,
            fetchingData: fetchingData ? { ...fetchingData, tabIndex: getCurrentTabIndex() } : null,
          }}
        >
          {children}
        </TabContext.Provider>
      </div>
    </div>
  );
}
